import React, { useState, useEffect } from 'react';
import { Building2, ChevronDown } from 'lucide-react';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../contexts/AuthContext';

const BusinessSwitcher = () => {
  const [businesses, setBusinesses] = useState([]);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [memberships, setMemberships] = useState([]);

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user]);

  const fetchUserData = async () => {
    try {
      console.log('=== INICIO FETCH USER DATA ===');
      console.log('Usuario actual:', user?.id);
      
      // 1. Obtener todos los perfiles del usuario
      const { data: userProfiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, auth_user_id')
        .eq('auth_user_id', user.id);

      if (profilesError) throw profilesError;
      
      if (!userProfiles || userProfiles.length === 0) {
        console.error('No se encontraron perfiles para el usuario');
        return;
      }
      
      console.log('Perfiles encontrados:', userProfiles);
      setProfiles(userProfiles);

      // 2. Obtener membresías y detalles de negocios
      const profileIds = userProfiles.map(profile => profile.id);
      
      const { data: businessMemberships, error: membershipsError } = await supabase
        .from('business_members')
        .select(`
          id,
          business_id,
          profile_id,
          role,
          businesses:business_id (
            id,
            name,
            description,
            owner_id
          )
        `)
        .in('profile_id', profileIds)
        .eq('status', 'active');

      if (membershipsError) throw membershipsError;
      
      console.log('Membresías encontradas:', businessMemberships);
      setMemberships(businessMemberships);
      
      // 3. Convertir a formato de negocios y añadir roles
      const businessesWithRoles = businessMemberships.map(item => ({
        ...item.businesses,
        role: item.role,
        membership_id: item.id,
        profile_id: item.profile_id
      }));
      
      setBusinesses(businessesWithRoles || []);
      console.log('Businesses con roles y profile_id:', businessesWithRoles);

      // 4. Identificar el negocio actual (usando metadata del usuario)
      if (businessesWithRoles.length > 0) {
        const currentId = user?.user_metadata?.businessId;
        console.log('ID de negocio actual en metadata:', currentId);
        
        if (currentId) {
          const current = businessesWithRoles.find(b => b.id === currentId);
          setCurrentBusiness(current || businessesWithRoles[0]);
          console.log('Negocio actual identificado:', current);
        } else {
          // Si no hay negocio actual en metadata, usar el primero
          setCurrentBusiness(businessesWithRoles[0]);
          console.log('Usando primer negocio como actual:', businessesWithRoles[0]);
        }
      }
      
      console.log('=== FIN FETCH USER DATA ===');
    } catch (err) {
      console.error('Error fetching user data:', err);
    }
  };

  const switchBusiness = async (business) => {
    try {
      console.log('=== DEBUG SWITCH BUSINESS ===');
      console.log('Cambiando a negocio:', business);
      
      // Buscar el profile_id correcto para este usuario y negocio
      const membership = memberships.find(m => 
        m.business_id === business.id && 
        profiles.some(p => p.id === m.profile_id && p.auth_user_id === user.id)
      );
      
      if (!membership) {
        console.error('No se encontró la membresía correspondiente');
        throw new Error('No se encontró la membresía para este negocio');
      }
      
      const profileId = membership.profile_id;
      console.log('ProfileID para actualizar:', profileId);
      
      // 1. Actualizar perfil en Supabase (mantener snake_case para la BD)
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          business_id: business.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', profileId); // ¡Usar el profileId correcto!
      
      if (profileError) {
        console.error('Error actualizando perfil:', profileError);
        throw profileError;
      }
      
      console.log('✅ Perfil actualizado');

      // 2. Actualizar metadata del usuario (usar camelCase)
      const { data: { user: updatedUser }, error: userError } = await supabase.auth.updateUser({
        data: { 
          businessId: business.id,
          currentBusinessName: business.name
        }
      });

      if (userError) {
        console.error('Error actualizando usuario:', userError);
        throw userError;
      }

      console.log('✅ Usuario actualizado:', updatedUser);

      // 3. Actualizar estado local
      setCurrentBusiness(business);
      setIsOpen(false);

      // 4. Forzar una actualización de la sesión
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error('Error obteniendo sesión:', sessionError);
        throw sessionError;
      }

      // 5. Verificar que los cambios se aplicaron
      console.log('🔍 Verificando cambios:');
      console.log('- Session user:', session?.user);
      console.log('- Metadata:', session?.user?.user_metadata);
      console.log('- Business ID:', session?.user?.user_metadata?.businessId);

      // 6. Si todo está bien, recargar la página
      console.log('🔄 Recargando página...');
      window.location.reload();
    } catch (err) {
      console.error('Error switching business:', err);
      alert('Error al cambiar de negocio. Por favor intenta de nuevo.');
    }
  };

  // Si no hay usuario o negocios, mostrar un estado por defecto
  if (!user || businesses.length === 0) {
    return (
      <div className="px-4 py-3 border-b dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
            <Building2 className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </div>
          <div>
            <div className="font-medium text-gray-400 dark:text-gray-500">Sin negocio</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Seleccionar negocio</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 border-b dark:border-gray-700">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
            <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-300" />
          </div>
          <div>
            <div className="font-medium dark:text-white">
              {currentBusiness ? currentBusiness.name : businesses[0].name}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {currentBusiness?.role === 'owner' ? 'Dueño' : 'Miembro'}
            </div>
          </div>
        </div>
        {businesses.length > 1 && (
          <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {isOpen && businesses.length > 1 && (
        <>
          <div 
            className="fixed inset-0 z-20"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-4 right-4 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 py-1 z-30">
            {businesses
              .filter(b => b.id !== (currentBusiness?.id || businesses[0].id))
              .map(business => (
                <button
                  key={business.id}
                  onClick={() => switchBusiness(business)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3"
                >
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div>
                    <div className="font-medium dark:text-gray-200">{business.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {business.role === 'owner' ? 'Dueño' : 'Miembro'}
                    </div>
                  </div>
                </button>
              ))}
          </div>
        </>
      )}
    </div>
  );
};

export default BusinessSwitcher; 